// ============================================================
// VCS.MediaCache — IndexedDB-basiertes Medien-Caching
// ============================================================
// Cached Bilder, Videos und Audio lokal im Browser (IndexedDB).
// Reduziert Server-Requests und beschleunigt insbesondere
// E2E-verschlüsselte Medien (kein erneutes fetch+decrypt nötig).
// ============================================================
window.VCS = window.VCS || {};

VCS.MediaCache = class {

    constructor() {
        this._db = null;
        this._blobUrls = new Map();   // serverUrl → objectURL
        this._pending = new Map();    // serverUrl → Promise<blobUrl>
        this._dbName = 'vcs-media-cache';
        this._dbVersion = 1;
        this._storeName = 'media';
        this._maxBytes = 200 * 1024 * 1024; // 200 MB default
        this._maxVideoBytes = 50 * 1024 * 1024; // groessere Videos nicht speichern
        this._videoFetches = new Set();       // laufende Hintergrund-Downloads (storeVideo)
    }

    // ── Init ──────────────────────────────────────────────────

    async init() {
        const custom = parseInt(localStorage.getItem('media-cache-limit'));
        if (custom > 0) this._maxBytes = custom * 1024 * 1024;

        try {
            this._db = await this._openDb();
            // Startup-GC: alte Einträge aufräumen
            this._evict().catch(() => {});
            // Apple-Geraete (iOS/iPadOS/Mac): keine Videos im Cache. Blob-Videos
            // verursachten dort errCode=4 beim Replay -> Restbestaende entfernen.
            if (!this.canCacheVideo()) {
                this._evictVideoEntries().catch(() => {});
            }
        } catch (e) {
            console.warn('[MediaCache] IndexedDB nicht verfügbar:', e.message);
            this._db = null;
        }
    }

    // ── Öffentliche API ───────────────────────────────────────

    /**
     * Blob-URL für eine Server-URL zurückgeben.
     * Cache-Hit → sofort, Cache-Miss → fetch + speichern.
     * @param {string} url - Server-URL (z.B. https://vcs.../api/media/uuid.jpg)
     * @returns {Promise<string>} Blob-URL oder Original-URL als Fallback
     */
    async get(url) {
        if (!url || url === 'pending') return url;

        // 1. Bereits aktive Blob-URL im RAM?
        if (this._blobUrls.has(url)) return this._blobUrls.get(url);

        // 2. Kein IndexedDB → Original-URL
        if (!this._db) return url;

        // 3. Parallelen Fetch verhindern
        if (this._pending.has(url)) return this._pending.get(url);

        const promise = this._resolve(url);
        this._pending.set(url, promise);
        try {
            return await promise;
        } finally {
            this._pending.delete(url);
        }
    }

    /**
     * Blob explizit im Cache speichern (z.B. nach E2E-Entschlüsselung).
     * @param {string} url - Server-URL als Key
     * @param {Blob} blob - Der zu cachende Blob
     * @param {object} [opts] - { encrypted: false, mimeType: null }
     * @returns {Promise<string>} Blob-URL
     */
    async put(url, blob, opts = {}) {
        if (!url || !blob || !this._db) return null;

        // Videos nur ausserhalb von Apple-Geraeten und bis zur Groessengrenze.
        // null -> Aufrufer nutzt eigene Blob-URL bzw. Server-URL.
        const putMime = opts.mimeType || blob.type || '';
        if (putMime.startsWith('video/') && (!this.canCacheVideo() || blob.size > this._maxVideoBytes)) {
            return null;
        }

        const blobUrl = URL.createObjectURL(blob);
        this._blobUrls.set(url, blobUrl);

        try {
            const tx = this._db.transaction(this._storeName, 'readwrite');
            const store = tx.objectStore(this._storeName);
            await this._req(store.put({
                url,
                blob,
                size: blob.size,
                mimeType: opts.mimeType || blob.type || 'application/octet-stream',
                lastAccess: Date.now(),
                createdAt: Date.now(),
                encrypted: !!opts.encrypted,
            }));
        } catch (e) {
            console.warn('[MediaCache] put Fehler:', e.message);
        }

        // Video-Thumbnail im Hintergrund erzeugen
        const mime = opts.mimeType || blob.type || '';
        if (mime.startsWith('video/')) {
            this.generateThumbnail(url, blob).catch(() => {});
        }

        // GC im Hintergrund
        this._evict().catch(() => {});
        return blobUrl;
    }

    /**
     * Prüft ob eine URL im Cache ist (ohne Blob zu laden).
     */
    async has(url) {
        if (!this._db || !url) return false;
        if (this._blobUrls.has(url)) return true;
        try {
            const tx = this._db.transaction(this._storeName, 'readonly');
            const store = tx.objectStore(this._storeName);
            const count = await this._req(store.count(url));
            return count > 0;
        } catch (_) {
            return false;
        }
    }

    /**
     * Duerfen Videos lokal gespeichert werden?
     * Nein auf allen Apple-Geraeten (iPhone, iPad, Mac): Safari/WebKit spielte
     * Blob-Videos beim Replay nicht ab (errCode=4). iPadOS meldet sich als
     * "Macintosh", iPhone-UAs enthalten "like Mac OS X" -> beides abgedeckt.
     */
    canCacheVideo() {
        return !this.isAppleDevice();
    }

    /**
     * iPhone, iPad oder Mac (jeder Browser)?
     */
    isAppleDevice() {
        const ua = navigator.userAgent || '';
        return /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(ua);
    }

    /**
     * Blob-URL nur aus dem Cache holen — nie nachladen.
     * @param {string} url - Server-URL als Key
     * @param {object} [opts] - { decryptedOnly: true } liefert nur Eintraege,
     *        die per put(..., {encrypted:true}) als entschluesselt gespeichert wurden
     *        (schuetzt vor verschluesselten Bytes, die get() frueher abgelegt hat)
     * @returns {Promise<string|null>} Blob-URL oder null
     */
    async getCached(url, opts = {}) {
        if (!url || !this._db) return null;
        if (this._blobUrls.has(url) && !opts.decryptedOnly) return this._blobUrls.get(url);

        try {
            const tx = this._db.transaction(this._storeName, 'readwrite');
            const store = tx.objectStore(this._storeName);
            const entry = await this._req(store.get(url));
            if (!entry || !entry.blob) return null;
            if (opts.decryptedOnly && !entry.encrypted) return null;

            const mime = entry.mimeType || '';
            // Videos auf Apple-Geraeten nie aus dem Cache abspielen
            if (mime.startsWith('video/') && !this.canCacheVideo()) return null;

            // Auto-Recovery: gespeicherten MIME gegen URL-Endung gegenchecken.
            // Alte Caches (vor Server-Patch) enthalten teils application/octet-stream
            // → Safari errCode=4 beim Playback. Solche Einträge verwerfen.
            // Entschluesselte E2E-Eintraege: MIME setzen wir selbst, URL-Endung passt nicht.
            if (!entry.encrypted && !this._isMimeValid(url, mime)) {
                const shortUrl = String(url).slice(-80);
                const storedMime = mime || '(empty)';
                console.warn('[MediaCache] invalid cached MIME for', shortUrl, '→', storedMime, '— evicting');
                this._wsLog('warn', `evict-invalid-mime url="${shortUrl}" stored="${storedMime}"`);
                try { store.delete(url); } catch (_) {}
                this._revokeSingle(url);
                return null;
            }

            // lastAccess aktualisieren
            entry.lastAccess = Date.now();
            store.put(entry);

            if (this._blobUrls.has(url)) return this._blobUrls.get(url);
            const blobUrl = URL.createObjectURL(entry.blob);
            this._blobUrls.set(url, blobUrl);
            // Thumbnail nachholen falls noch nicht vorhanden
            if (mime.startsWith('video/')) {
                this.generateThumbnail(url, entry.blob).catch(() => {});
            }
            return blobUrl;
        } catch (_) {
            return null;
        }
    }

    /**
     * Unverschluesseltes Video im Hintergrund komplett laden und speichern.
     * Wird beim ersten Abspielen aufgerufen; der Player streamt parallel weiter
     * vom Server, ab dem naechsten Oeffnen kommt das Video aus dem Cache.
     * @param {string} url - Server-URL des Videos
     */
    async storeVideo(url) {
        if (!url || !this._db || !this.canCacheVideo()) return;
        if (url.startsWith('blob:') || this._videoFetches.has(url)) return;
        if (await this.has(url)) return;

        this._videoFetches.add(url);
        const shortUrl = String(url).slice(-80);
        try {
            const resp = await fetch(url);
            if (!resp.ok) {
                this._wsLog('warn', `video-store fetch failed url="${shortUrl}" status=${resp.status}`);
                return;
            }
            const len = parseInt(resp.headers.get('content-length') || '0', 10);
            if (len > this._maxVideoBytes) {
                try { resp.body?.cancel?.(); } catch (_) {}
                this._wsLog('info', `video-store skipped (too large) url="${shortUrl}" bytes=${len}`);
                return;
            }
            const blob = await resp.blob();
            const serverMime = (resp.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
            const mime = serverMime || (blob.type || '').toLowerCase();
            if (!mime.startsWith('video/') || !this._isMimeValid(url, mime)) {
                this._wsLog('warn', `video-store skipped (bad mime) url="${shortUrl}" mime="${mime || '(empty)'}"`);
                return;
            }
            const typedBlob = blob.type === mime ? blob : new Blob([blob], { type: mime });
            const blobUrl = await this.put(url, typedBlob, { mimeType: mime });
            if (blobUrl) {
                this._wsLog('info', `video-store ok url="${shortUrl}" bytes=${typedBlob.size}`);
            } else {
                this._wsLog('info', `video-store skipped (put refused) url="${shortUrl}" bytes=${typedBlob.size}`);
            }
        } catch (e) {
            this._wsLog('warn', `video-store error url="${shortUrl}" msg="${e?.message || e}"`);
        } finally {
            this._videoFetches.delete(url);
        }
    }

    /**
     * Einzelnen Eintrag löschen.
     */
    async remove(url) {
        this._revokeSingle(url);
        if (!this._db) return;
        try {
            const tx = this._db.transaction(this._storeName, 'readwrite');
            tx.objectStore(this._storeName).delete(url);
        } catch (_) {}
    }

    /**
     * Gesamten Cache leeren.
     */
    async clear() {
        this.revokeAll();
        if (!this._db) return;
        try {
            const tx = this._db.transaction(this._storeName, 'readwrite');
            tx.objectStore(this._storeName).clear();
        } catch (_) {}
    }

    /**
     * Cache-Statistiken.
     */
    async getStats() {
        if (!this._db) return { totalSize: 0, entryCount: 0, maxBytes: this._maxBytes };
        try {
            const tx = this._db.transaction(this._storeName, 'readonly');
            const store = tx.objectStore(this._storeName);
            const all = await this._req(store.getAll());
            let totalSize = 0;
            for (const entry of all) totalSize += entry.size || 0;
            return { totalSize, entryCount: all.length, maxBytes: this._maxBytes };
        } catch (_) {
            return { totalSize: 0, entryCount: 0, maxBytes: this._maxBytes };
        }
    }

    /**
     * Alle aktiven Object-URLs freigeben (z.B. bei Chat-Wechsel).
     * IndexedDB-Einträge bleiben erhalten.
     */
    revokeAll() {
        for (const blobUrl of this._blobUrls.values()) {
            try { URL.revokeObjectURL(blobUrl); } catch (_) {}
        }
        this._blobUrls.clear();
    }

    // ── Video-Thumbnails ──────────────────────────────────────

    /**
     * Thumbnail-URL für ein Video zurückgeben (aus Cache).
     * @param {string} videoUrl - Original-Video-URL
     * @returns {Promise<string|null>} Blob-URL des Thumbnails oder null
     */
    async getThumbnail(videoUrl) {
        if (!videoUrl || !this._db) return null;
        const thumbKey = 'thumb::' + videoUrl;
        if (this._blobUrls.has(thumbKey)) return this._blobUrls.get(thumbKey);
        try {
            const tx = this._db.transaction(this._storeName, 'readonly');
            const store = tx.objectStore(this._storeName);
            const entry = await this._req(store.get(thumbKey));
            if (entry && entry.blob) {
                const blobUrl = URL.createObjectURL(entry.blob);
                this._blobUrls.set(thumbKey, blobUrl);
                return blobUrl;
            }
        } catch (_) {}
        return null;
    }

    /**
     * Thumbnail aus Video-Blob erzeugen und in IndexedDB speichern.
     * Extrahiert das erste sichtbare Frame via Canvas.
     * @param {string} videoUrl - Original-Video-URL (als Key)
     * @param {Blob|string} videoSource - Video-Blob oder Blob-URL
     */
    async generateThumbnail(videoUrl, videoSource) {
        if (!videoUrl || !this._db) return;
        const thumbKey = 'thumb::' + videoUrl;
        // Schon vorhanden?
        if (this._blobUrls.has(thumbKey)) return;
        try {
            const tx = this._db.transaction(this._storeName, 'readonly');
            const count = await this._req(tx.objectStore(this._storeName).count(thumbKey));
            if (count > 0) return;
        } catch (_) {}

        try {
            const blob = await this._extractVideoFrame(videoSource);
            if (!blob) return;
            const blobUrl = URL.createObjectURL(blob);
            this._blobUrls.set(thumbKey, blobUrl);
            const storeTx = this._db.transaction(this._storeName, 'readwrite');
            await this._req(storeTx.objectStore(this._storeName).put({
                url: thumbKey,
                blob,
                size: blob.size,
                mimeType: 'image/jpeg',
                lastAccess: Date.now(),
                createdAt: Date.now(),
                encrypted: false,
            }));
        } catch (e) {
            console.warn('[MediaCache] Thumbnail-Generierung fehlgeschlagen:', e.message);
        }
    }

    /**
     * Erstes sichtbares Frame eines Videos als JPEG-Blob extrahieren.
     */
    _extractVideoFrame(videoSource) {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;
            video.preload = 'auto';
            let tempBlobUrl = null;
            // Timeout: falls Video nicht ladbar
            const timeout = setTimeout(() => { cleanup(); resolve(null); }, 8000);

            const cleanup = () => {
                clearTimeout(timeout);
                video.removeAttribute('src');
                video.load();
                if (tempBlobUrl) {
                    URL.revokeObjectURL(tempBlobUrl);
                    tempBlobUrl = null;
                }
            };

            const captureFrame = () => {
                try {
                    const canvas = document.createElement('canvas');
                    // Max 320px breit, Seitenverhältnis beibehalten
                    const scale = Math.min(1, 320 / (video.videoWidth || 320));
                    canvas.width = Math.round((video.videoWidth || 320) * scale);
                    canvas.height = Math.round((video.videoHeight || 240) * scale);
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    canvas.toBlob((blob) => {
                        cleanup();
                        resolve(blob);
                    }, 'image/jpeg', 0.7);
                } catch (e) {
                    cleanup();
                    resolve(null);
                }
            };

            video.addEventListener('seeked', captureFrame, { once: true });

            video.addEventListener('loadedmetadata', () => {
                const seekTo = Math.min(0.5, video.duration || 0);
                if (seekTo > 0) {
                    // Zum Frame bei 0.5s springen (vermeidet schwarzes Intro)
                    video.currentTime = seekTo;
                } else {
                    // Video zu kurz oder duration unbekannt → erstes Frame direkt capturen
                    captureFrame();
                }
            }, { once: true });

            video.addEventListener('error', () => { cleanup(); resolve(null); }, { once: true });

            if (videoSource instanceof Blob) {
                tempBlobUrl = URL.createObjectURL(videoSource);
                video.src = tempBlobUrl;
            } else {
                video.src = videoSource;
            }
        });
    }

    // ── Private ───────────────────────────────────────────────

    /**
     * Prüft, ob ein gespeicherter MIME-Typ plausibel zur URL/Endung passt.
     * Schützt vor altem `application/octet-stream`-Cache, der Safari beim
     * Audio-Playback mit MEDIA_ERR_SRC_NOT_SUPPORTED (errCode=4) abschießt.
     * @param {string} url
     * @param {string} mimeType
     * @returns {boolean}
     */
    _isMimeValid(url, mimeType) {
        // Leer/unbekannt/octet-stream → ungültig
        if (!mimeType) return false;
        const mt = String(mimeType).toLowerCase();
        if (mt === 'application/octet-stream') return false;

        // thumb::-Keys: muss image sein (siehe generateThumbnail → image/jpeg)
        if (typeof url === 'string' && url.indexOf('thumb::') === 0) {
            return mt.startsWith('image/');
        }

        // Endung aus URL ziehen (Query/Hash strippen)
        const clean = String(url).split('?')[0].split('#')[0];
        const dot = clean.lastIndexOf('.');
        if (dot < 0 || dot < clean.length - 6) {
            // Keine Endung erkennbar → tolerant: alles außer octet-stream durchlassen
            return true;
        }
        const ext = clean.slice(dot + 1).toLowerCase();

        const expected = {
            m4a:  ['audio/mp4', 'audio/x-m4a', 'audio/mpeg'],
            mp3:  ['audio/mpeg', 'audio/mp3'],
            aac:  ['audio/aac', 'audio/x-aac'],
            wav:  ['audio/wav', 'audio/wave', 'audio/x-wav'],
            ogg:  ['audio/ogg', 'application/ogg'],
            webm: ['audio/webm', 'video/webm'],
            mp4:  ['video/mp4', 'audio/mp4'],
            mov:  ['video/quicktime', 'video/mp4'],
            jpg:  ['image/jpeg'],
            jpeg: ['image/jpeg'],
            png:  ['image/png'],
            webp: ['image/webp'],
            gif:  ['image/gif'],
        };

        const allowed = expected[ext];
        if (!allowed) {
            // Unbekannte Endung → tolerant: nur klar kaputtes octet-stream blocken
            return true;
        }
        return allowed.indexOf(mt) >= 0;
    }

    /**
     * WS-Diagnose-Log über das gleiche client-log-Pattern wie chat-voice.js.
     * In pm2-Logs greppbar via "MediaCache".
     */
    _wsLog(level, msg) {
        try {
            window.VCS?.signaling?.send?.({
                type: 'client-log',
                level: level || 'info',
                msg: '[MediaCache] ' + msg,
                ua: (navigator.userAgent || '').slice(0, 60),
            });
        } catch (_) {}
    }

    async _resolve(url) {
        // Aus IndexedDB laden (inkl. MIME-Auto-Recovery, siehe getCached)
        const cachedUrl = await this.getCached(url);
        if (cachedUrl) return cachedUrl;

        // Cache-Miss (oder Auto-Recovery): fetch → speichern
        try {
            const resp = await fetch(url);
            if (!resp.ok) return url;
            const blob = await resp.blob();
            if (blob.size < 100) return url; // zu klein, nicht cachen

            // Defensive: Server-MIME prüfen, bevor wir den Fehler wieder einbacken.
            // Wenn Server (bug-warts) octet-stream/leer liefert, blob nicht persistieren —
            // statt dessen direkt eine Blob-URL mit Original-Server-MIME zurückgeben.
            const serverMime = (resp.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
            const blobMime = (blob.type || '').toLowerCase();
            const effectiveMime = serverMime || blobMime;
            if (!this._isMimeValid(url, effectiveMime)) {
                const shortUrl = String(url).slice(-80);
                console.warn('[MediaCache] server returned invalid MIME for', shortUrl, '→', effectiveMime || '(empty)', '— skipping cache');
                this._wsLog('warn', `skip-cache-bad-mime url="${shortUrl}" server="${effectiveMime || '(empty)'}"`);
                return url;
            }

            const blobUrl = await this.put(url, blob);
            // Video-Thumbnail im Hintergrund erzeugen
            if (blob.type && blob.type.startsWith('video/')) {
                this.generateThumbnail(url, blob).catch(() => {});
            }
            return blobUrl || url;
        } catch (_) {
            return url;
        }
    }

    _openDb() {
        return new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                return reject(new Error('IndexedDB nicht unterstützt'));
            }
            const req = indexedDB.open(this._dbName, this._dbVersion);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(this._storeName)) {
                    const store = db.createObjectStore(this._storeName, { keyPath: 'url' });
                    store.createIndex('by-lastAccess', 'lastAccess', { unique: false });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }

    async _evict() {
        if (!this._db) return;
        try {
            const stats = await this.getStats();
            if (stats.totalSize <= this._maxBytes) return;

            const target = this._maxBytes * 0.8;
            let freed = 0;
            const toDelete = [];

            const tx = this._db.transaction(this._storeName, 'readonly');
            const store = tx.objectStore(this._storeName);
            const index = store.index('by-lastAccess');
            const cursor = index.openCursor(); // aufsteigend = älteste zuerst

            await new Promise((resolve, reject) => {
                cursor.onsuccess = (e) => {
                    const c = e.target.result;
                    if (!c || (stats.totalSize - freed) <= target) {
                        resolve();
                        return;
                    }
                    toDelete.push(c.value.url);
                    freed += c.value.size || 0;
                    c.continue();
                };
                cursor.onerror = () => reject(cursor.error);
            });

            if (toDelete.length > 0) {
                const delTx = this._db.transaction(this._storeName, 'readwrite');
                const delStore = delTx.objectStore(this._storeName);
                for (const key of toDelete) {
                    this._revokeSingle(key);
                    delStore.delete(key);
                }
            }
        } catch (e) {
            console.warn('[MediaCache] evict Fehler:', e.message);
        }
    }

    _revokeSingle(url) {
        const blobUrl = this._blobUrls.get(url);
        if (blobUrl) {
            try { URL.revokeObjectURL(blobUrl); } catch (_) {}
            this._blobUrls.delete(url);
        }
    }

    async _evictVideoEntries() {
        if (!this._db) return;
        try {
            const toDelete = [];
            const tx = this._db.transaction(this._storeName, 'readonly');
            const store = tx.objectStore(this._storeName);
            const cursor = store.openCursor();

            await new Promise((resolve, reject) => {
                cursor.onsuccess = (e) => {
                    const c = e.target.result;
                    if (!c) { resolve(); return; }
                    const mime = (c.value.mimeType || '').toLowerCase();
                    if (mime.startsWith('video/')) {
                        toDelete.push({ url: c.value.url, size: c.value.size || 0 });
                    }
                    c.continue();
                };
                cursor.onerror = () => reject(cursor.error);
            });

            if (toDelete.length > 0) {
                const delTx = this._db.transaction(this._storeName, 'readwrite');
                const delStore = delTx.objectStore(this._storeName);
                let totalBytes = 0;
                for (const entry of toDelete) {
                    this._revokeSingle(entry.url);
                    delStore.delete(entry.url);
                    totalBytes += entry.size;
                }
                this._wsLog('info', 'evicted ' + toDelete.length + ' video entries (' + Math.round(totalBytes / 1024) + ' KB)');
            }
        } catch (e) {
            console.warn('[MediaCache] video-evict Fehler:', e.message);
        }
    }

    /** IDBRequest → Promise Helper */
    _req(request) {
        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }
};
